// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { func, bool, oneOfType, string } from 'prop-types';
import { Button, Loading } from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import uniqueId from 'lodash/uniqueId';
import { useContextSelector } from 'use-context-selector';
import { Package, Copy, SquareArrowOutUpRight, Megaphone, SquareStack, PackageOpen, Eye } from 'lucide-react';

import Img from 'Containers/Image/Image';
import ProductPropType from 'Types/product';
import { renderStock } from 'Components/Products/helpers';
import AddToWishlistMenu from 'Containers/Wishlists/AddToWishlistMenu';
import useNumberUtils from 'Hooks/useNumberUtils';
import ProductPromotions from 'Components/Products/ProductPromotions';
import ProductPriceRequest from 'Containers/Products/ProductPriceRequest';
import Price from 'Components/Price/Price';
import Badges from 'Components/Badges/Badges';
import AddToCart from 'Components/AddToCart/AddToCart';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';
import { StyledProductListItem } from 'Components/Products/StyledProductItems';
import useToastNotifications from 'Hooks/useToastNotifications';
import copyToClipboard from 'Utils/copyToClipboard';
import highlightText from 'Utils/highlightText';
import IQuote from 'Components/IQuote/IQuote';
import { UserContext } from 'Contexts/UserContext';
import QuantityBasedPrice from 'Components/QuantityBasedPrice/QuantityBasedPrice';

const ProductListItem = ({
  product,
  addToListLoading,
  addToCartLoading,
  promotionLoading,
  onStockAlertModalOpen,
  onAddToWishlist,
  onChangeQuantity,
  onQuickView,
  onAddToCart
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatNumber } = useNumberUtils();
  const userData = useContextSelector(UserContext, ({ data }) => data);
  const currency = userData?.defaultCurrency;
  const withVat = userData?.withVat;
  const accountManagerPhone = userData?.accountManager?.phone;
  const toast = useToastNotifications();

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  const memoizedPromotions = useMemo(
    () => Object
      .entries(product?.promotions || {})
      .map(([type, promotions]) => (
        <ProductPromotions
          key={ uniqueId(type) }
          type={ type }
          loading={ promotionLoading }
          promotions={ promotions }
          onAddToCart={ onAddToCart }
          onQuickView={ onQuickView }
          product={ product }
          withVat={ withVat }
        />
      )),
    [product, promotionLoading]
  );

  return (
    <>
      <StyledProductListItem theme={ theme } className="productListItem">
        <div className="preview">
          { !product?.hasPriceRequest && !product.warnings?.blocked_prices && (
            <Badges
              isEol={ product.isEol }
              isOnDemandOnly={
                // eslint-disable-next-line no-nested-ternary
                product.reservedStockValue > 0
                  ? false
                  : product.stockValue <= 0
                    ? product.isOnDemandOnly
                    : false
              }
              isResealed={ !!product.originalCode }
              isNew={ product.isNew }
              savings={ Math.round(((product.price - product.promoPrice) / product.price) * 100) / 100 }
              isSupplierOffer={ !product?.hasPrice }
              hasSupplierOffers={ product.hasSupplierOffers }
            />
          ) }
          <Link
            to={ `/products/${ product.slug }/pd/${ product.id }` }
            state={ {
              component: 'product_list_item',
              source: 'product_list_page',
              clickedOn: 'image',
              productId: product.id
            } }
          >
            <Img
              className="image"
              src={ product?.pictures?.at(0)?.urlThumbnailPicture }
              unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
              loader={ <Loading active withOverlay={ false } small /> }
              loading="lazy"
              alt={ product.name }
            />
          </Link>
        </div>
        <div className="details">
          <div className="info">
            <Link
              className="title"
              to={ `/products/${ product.slug }/pd/${ product.id }` }
              state={ {
                component: 'product_list_item',
                source: 'product_list_page',
                clickedOn: 'title',
                productId: product.id
              } }
            >
              { highlightText(product.title, product?.highlighted?.title) }
            </Link>
            <p>
              { t('products.code') }:
              <span>{ product.code }</span>
              <span><Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(product?.code || '') } /></span>
            </p>
            <p>
              { t('products.vendor') }:
              <span>{ product.manufacturerName }</span>
            </p>
            <p>
              { t('products.warranty') }:
              <span>{ product.warranty }</span>
            </p>
            { !product?.warnings?.blocked_stocks && (
              renderStock({ t, product, info: { accountManagerPhone } })
            ) }
            { (!product?.hasPrice || product?.hasSupplierOffers) && (
              <DeliveryEstimate
                estimate={ product?.supplierOfferDeliveryTimeDays }
                isExternalSupplier={ !product?.hasPrice }
              />
            ) }
            <div className="helperButtons">
              <Button
                onClick={ onQuickView }
                kind="primary"
                className="green"
                data-test="onQuickView"
                data-testid={ product.productId }
              >
                <Eye />
                { t('products.quickView') }
              </Button>
              { product.inCsp && (
                <div className="csp">
                  <SquareArrowOutUpRight />
                  <a href="https://bcp.nod.ro/login" target="_blank">{ t('products.continueCsp') }</a>
                </div>
              ) }
              { product.hasResealed && (
                <p className="resealed">
                  <PackageOpen /> { product?.resealedCount || '' } { t('products.resealed') }
                </p>
              ) }
              {
                product.inIquote && <IQuote product={ product } />
              }
            </div>
          </div>
          <div className="priceContainer">
            { !product?.hasPriceRequest && !product.warnings?.blocked_prices && (
              <Badges
                specialPriceValidTo={ product.specialPriceValidTo }
                specialPrice={
                  product.specialPrice && !product.isResealed
                    ? (product.price !== product.promoPrice && ((product.price - product.promoPrice) / product.price) >= 0.01)
                    : false
                }
                fixedPrice={ product.fixedPrice }
                isClearance={ product.stockClearingPrice }
              />
            ) }
            { (!product?.hasPriceRequest && !product.warnings?.blocked_prices) && (
              <Price
                price={ product.price }
                promoPrice={ product.promoPrice }
                withTooltip
                withVat={ withVat }
                displayVatInfo
                tooltipContent={ `
                ${ t('products.greenStamp') }
                  ${ formatNumber(product.greenStampValue, { currency }) }
              ` }
              />
            ) }
            { (
              !!product?.priceScales?.length
              && !(product?.warnings?.blocked_orders || product?.warnings?.blocked_prices)
            ) && <QuantityBasedPrice priceScales={ product.priceScales } visibleItems={ 3 } /> }
            { product.minQuantity > 1 && (
              <p className="minQuantity">
                <SquareStack />
                { t('products.multipleOf', { multiple: product.minQuantity }) }
              </p>
            ) }
            { /* eslint-disable no-nested-ternary */ }
            { product?.hasPriceRequest ? <ProductPriceRequest productId={ product.id } /> : (
              ((product.stockValue === 0 || product.stockValue < product.minQuantity) && !Object.keys(product?.warnings || {}).length)
                ? (
                  <Button
                    data-test="onStockAlert"
                    data-testid={ product.productId }
                    className="red stockAlert"
                    kind="danger--ghost"
                    onClick={ onStockAlertModalOpen }
                  >
                    <Megaphone />
                    { t('products.stockAlert') }
                  </Button>
                )
                : (
                  <AddToCart
                    onClick={ onAddToCart }
                    trackingContext={ {
                      location: 'product_list_item',
                      product_sku: product?.code,
                    } }
                    onChange={ onChangeQuantity }
                    loading={ addToCartLoading }
                    disabled={
                      addToCartLoading
                      || product.warnings?.blocked_prices
                      || product.warnings?.blocked_stocks
                      || product.warnings?.blocked_orders
                    }
                    showSelector={ !(product.warnings?.blocked_prices || product.warnings?.blocked_stocks || product.warnings?.blocked_orders) }
                    min={ product.minQuantity }
                    max={ product.stockValue }
                    value={ product.quantity }
                    step={ product.minQuantity }
                    id={ product.id }
                    size="sm"
                  />
                )
              )}
            <AddToWishlistMenu
              onAddToWishlist={ onAddToWishlist }
              loading={ addToListLoading }
              canCreate
              product={ product }
            />
          </div>
        </div>
      </StyledProductListItem>
      { memoizedPromotions }
    </>
  );
};

ProductListItem.propTypes = {
  product: ProductPropType,
  addToListLoading: bool,
  addToCartLoading: bool,
  promotionLoading: oneOfType([string, bool]),
  onAddToCart: func,
  onQuickView: func,
  onChangeQuantity: func,
  onAddToWishlist: func,
  onStockAlertModalOpen: func
};

export default ProductListItem;
